import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PortalService } from './core/portal.service';
import { I18n } from './core/i18n/i18n.service';
import { LangCode, sitePath } from './core/i18n/languages';

/** Pages that open on a dark hero, so the header goes dark over it. */
const DARK_HEROES = ['/', '/hotels', '/resellers', '/creators', '/custom'];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:click)': 'followLink($event)' },
  template: `
    <header class="topbar no-print" [class.dark]="darkHeader()">
      <div class="wrap bar">
        <a class="brand" href="/">
          <img [src]="darkHeader() ? '/assets/images/alltours/logo_white.png' : '/assets/images/alltours/logo.png'" alt="" width="30" height="29" />
          <span class="wordmark">
            <span class="word">AudaTours</span>
            <span class="sub">{{ t().nav.brandSub }}</span>
          </span>
        </a>
        <nav class="links">
          <a href="/hotels" [class.on]="path() === '/hotels' || path() === '/posters'">{{ t().nav.hotels }}</a>
          <a href="/resellers" [class.on]="path() === '/resellers'">{{ t().nav.resellers }}</a>
          <a [href]="groupsUrl()">{{ t().nav.groups }}</a>
          <a href="/creators" [class.on]="path() === '/creators'">{{ t().nav.creators }}</a>
          <a href="/custom" [class.on]="path() === '/custom'">{{ t().nav.custom }}</a>
        </nav>
        <div class="actions">
          <a class="site" [href]="'https://audatours.com' + sitePath(i18n.lang(), '/')" target="_blank" rel="noopener" [attr.aria-label]="t().nav.findTour" [title]="t().nav.findTour" data-testid="main-site">
            <span class="icon">travel_explore</span><span class="site-label">{{ t().nav.findTour }}</span>
          </a>
          <label class="lang" [title]="t().meta.language">
            <span class="icon">language</span>
            <span class="lang-name">{{ i18n.language().nativeName }}</span>
            <select [value]="i18n.lang()" (change)="setLanguage($any($event.target).value)" [attr.aria-label]="t().meta.language" data-testid="ui-language">
              @for (l of i18n.languages; track l.code) {
                <option [value]="l.code" [selected]="l.code === i18n.lang()">{{ l.nativeName }}</option>
              }
            </select>
          </label>
          @if (portal.isPartner()) {
            <a class="btn btn-sm" [class.btn-lime]="darkHeader()" [class.btn-primary]="!darkHeader()" href="/dashboard">
              <span class="icon">space_dashboard</span> {{ t().nav.dashboard }}
            </a>
          } @else {
            @if (portal.authReady() && !portal.user()) {
              <a class="signin" href="/login">{{ t().nav.signIn }}</a>
            }
          }
        </div>
      </div>
    </header>

    <main><router-outlet /></main>

    <footer class="footer no-print">
      <div class="wrap foot">
        <div class="foot-brand">
          <img src="/assets/images/alltours/logo_white.png" alt="" width="28" height="27" />
          <div>
            <strong>{{ t().nav.footerTitle }}</strong>
            <p>{{ t().nav.footerBlurb }}</p>
          </div>
        </div>
        <div class="foot-links">
          <a href="/hotels">{{ t().nav.hotels }}</a>
          <a href="/resellers">{{ t().nav.resellers }}</a>
          <a [href]="groupsUrl()">{{ t().nav.groups }}</a>
          <a href="/creators">{{ t().nav.creators }}</a>
          <a href="/custom">{{ t().nav.custom }}</a>
          <a href="/posters">{{ t().nav.posterStudio }}</a>
          <a href="/dashboard">{{ t().nav.dashboard }}</a>
          <a href="/terms">{{ t().nav.partnerTerms }}</a>
          <a [href]="'https://audatours.com' + sitePath(i18n.lang(), '/')" target="_blank" rel="noopener">audatours.com</a>
          <a href="mailto:help@audatours.com">help&#64;audatours.com</a>
        </div>
      </div>
    </footer>
  `,
  styles: [`
    .topbar {
      position: sticky; top: 0; z-index: 50;
      background: rgba(248, 249, 240, .82);
      backdrop-filter: saturate(1.4) blur(14px);
      -webkit-backdrop-filter: saturate(1.4) blur(14px);
      border-bottom: 1px solid rgba(38, 48, 0, .07);
    }
    .topbar.dark { background: rgba(38, 48, 0, .86); border-bottom-color: rgba(255,255,255,.06); }
    .bar { display: flex; align-items: center; gap: 24px; height: 68px; }
    .brand { display: flex; align-items: center; gap: 9px; text-decoration: none; color: var(--olive); }
    .dark .brand { color: #fff; }
    .wordmark { display: flex; flex-direction: column; line-height: 1.05; }
    .word { font-family: var(--heading); font-weight: 700; font-size: 1.2rem; letter-spacing: -0.02em; }
    .sub {
      font-family: var(--heading); font-weight: 700; font-size: .62rem; letter-spacing: .14em; text-transform: uppercase;
      color: var(--olive-2, var(--olive)); opacity: .75; margin-top: 2px; white-space: nowrap;
    }
    .dark .sub { color: var(--lime); opacity: 1; }
    .site {
      display: flex; align-items: center; gap: 5px; text-decoration: none; white-space: nowrap;
      font-family: var(--heading); font-weight: 600; font-size: .9rem; color: var(--ink-2);
      padding: 6px 10px; border-radius: 10px; transition: background .15s;
    }
    .site .icon { font-size: 20px; color: var(--olive); }
    .site:hover { background: rgba(38, 48, 0, .06); }
    .dark .site { color: rgba(255,255,255,.8); }
    .dark .site:hover { background: rgba(255,255,255,.08); }
    .dark .site .icon { color: var(--lime); }
    .links { display: flex; gap: 6px; margin-left: 12px; }
    .links a {
      font-family: var(--heading); font-weight: 500; font-size: .95rem; text-decoration: none;
      color: var(--ink-2); padding: 8px 12px; border-radius: 10px; transition: background .15s, color .15s;
    }
    .links a:hover, .links a.on { color: var(--olive); background: rgba(38, 48, 0, .06); }
    .dark .links a { color: rgba(255,255,255,.75); }
    .dark .links a:hover, .dark .links a.on { color: #fff; background: rgba(255,255,255,.08); }
    .actions { margin-left: auto; display: flex; align-items: center; gap: 14px; }
    .lang {
      position: relative; display: flex; align-items: center; gap: 5px; cursor: pointer;
      font-family: var(--heading); font-weight: 600; font-size: .9rem; color: var(--ink-2);
      padding: 6px 10px; border-radius: 10px; transition: background .15s;
    }
    .lang:hover { background: rgba(38, 48, 0, .06); }
    .lang .icon { font-size: 20px; color: var(--olive); }
    .lang select { position: absolute; inset: 0; opacity: 0; cursor: pointer; font-size: 16px; }
    .dark .lang { color: rgba(255,255,255,.8); }
    .dark .lang:hover { background: rgba(255,255,255,.08); }
    .dark .lang .icon { color: var(--lime); }
    .signin { font-family: var(--heading); font-weight: 600; color: var(--olive); text-decoration: none; white-space: nowrap; }
    .dark .signin { color: #fff; }
    @media (max-width: 1180px) {
      .links { margin-left: 0; gap: 2px; }
      .links a { padding: 8px 8px; font-size: .9rem; }
    }
    @media (max-width: 1040px) {
      .links { display: none; }
      .bar { gap: 12px; }
      .lang-name { display: none; }
    }
    @media (max-width: 600px) {
      .site-label { display: none; }
      .actions { gap: 6px; }
    }
    @media (max-width: 420px) {
      .word { font-size: 1.05rem; }
      .actions .btn .icon { display: none; }
    }
    /* Holds the footer below the fold while a page is still loading. */
    main { min-height: 100vh; }
    .footer { background: var(--olive); color: rgba(255,255,255,.78); margin-top: 0; }
    .foot { display: flex; justify-content: space-between; gap: 32px; padding: 44px 0; flex-wrap: wrap; }
    .foot-brand { display: flex; gap: 14px; max-width: 420px; }
    .foot-brand strong { color: #fff; font-family: var(--heading); font-size: 1.05rem; }
    .foot-brand p { font-size: .92rem; margin-top: 4px; }
    .foot-links { display: flex; flex-wrap: wrap; gap: 10px 24px; align-items: center; }
    .foot-links a { color: var(--lime); text-decoration: none; font-weight: 600; font-size: .95rem; }
    .foot-links a:hover { text-decoration: underline; }
  `],
})
export class AppComponent {
  readonly portal = inject(PortalService);
  readonly i18n = inject(I18n);
  readonly t = this.i18n.t;
  readonly sitePath = sitePath;
  private router = inject(Router);
  readonly path = signal('/');
  readonly darkHeader = signal(false);
  readonly groupsUrl = computed(() => 'https://audatours.com' + sitePath(this.i18n.lang(), '/groups'));

  constructor() {
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(e => {
      const path = (e as NavigationEnd).urlAfterRedirects.split(/[?#]/)[0];
      this.path.set(path);
      this.darkHeader.set(DARK_HEROES.includes(path));
    });
  }

  /** Links within the portal (including ones inside translated text) move in-app instead of reloading the page. */
  followLink(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const a = (event.target as Element | null)?.closest('a');
    if (!(a instanceof HTMLAnchorElement) || a.target || a.hasAttribute('download')) return;
    const url = new URL(a.href);
    // ?lang= is read once at start-up, so those links still load the page.
    if (url.origin !== location.origin || url.searchParams.has('lang')) return;
    // A jump to a section of the page already open is left to the browser.
    if (url.hash && url.pathname === location.pathname && url.search === location.search) return;
    event.preventDefault();
    this.router.navigateByUrl(url.pathname + url.search + url.hash);
  }

  setLanguage(code: LangCode): void {
    this.i18n.use(code);
  }
}
